import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';

/** Any icon component (a lucide-react icon fits): rendered decoratively, aria-hidden. */
export type EmptyStateIcon = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

/** One "Ready to get started?" row: what to set up, why, and the button that does it */
export interface EmptyStep {
  icon?: EmptyStateIcon;
  title: string;
  /** One line */
  description: string;
  /** A Link or button ("Set up", "Import"), `button button-sm` */
  action: ReactNode;
}

export interface EmptyStateProps {
  /** One line, sentence case: what is empty ("No invoices yet"). */
  title: string;
  /** At most two lines: why it matters or what to do. */
  children?: ReactNode;
  /** The module's icon. Pass it in new code; without it, the icon of the current route's module is used. */
  icon?: EmptyStateIcon | undefined;
  /** v1: the primary action (a Link or button). Still supported. */
  action?: ReactNode;
  /** More actions, after `action` (a secondary button or link). */
  actions?: ReactNode;
  /** "Load sample data": calls `onLoad` with a busy state and shows its error, if any. */
  sample?: { label?: string; onLoad: () => unknown };
  /** A help article for this screen ("Learn more", new tab). Without it, an in-app "What is this?" for the screen's module is shown. */
  learnMoreHref?: string;
  /** The link text, e.g. "How invoices work" (default "Learn more"). */
  learnMoreLabel?: string;
  /** Heading level of the title (default 2). */
  headingLevel?: 2 | 3;
  /**
   * 2–3 ways to start, shown as a "Ready to get started?" card of action rows (icon, title, one
   * line, a button) instead of a lone icon (Plain's empty module). Use it on a module's first,
   * empty screen; `action` and `actions` still render under the card.
   */
  steps?: EmptyStep[];
}

// The module icon fallback, sample data, help and the sub-account gate load off the first load,
// as one chunk (onboarding/empty-extras.ts). Title, body and the screen's own actions render at
// once and never re-mount when it arrives.
const Extra = lazy(() => import('../onboarding/empty-extras'));

/**
 * Empty list or screen (v2, backward compatible with `{ title, children, action }`).
 * apps/web/README.md ("WP3: empty states") has the props and the copy rules.
 */
export function EmptyState(props: EmptyStateProps) {
  const { title, children, icon: Icon, action, actions } = props;
  const H = props.headingLevel === 3 ? 'h3' : 'h2';
  // No dead ends: "Choose a sub-account" with nothing to click becomes the picker or the create form
  return !(action || actions) && /^choose a sub-account/i.test(title) ? (
    <Suspense fallback={<EmptyState {...props} action=" " />}>
      <Extra part="gate" {...props} />
    </Suspense>
  ) : (
    <div className="state state-empty">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
        <Suspense fallback={null}>{Icon ? <Icon className="h-6 w-6" /> : <Extra part="icon" {...props} />}</Suspense>
      </span>
      <H className="state-title">{title}</H>
      {children ? <div className="m-0 max-w-md">{children}</div> : null}
      {props.steps?.length ? <Steps steps={props.steps} level={props.headingLevel === 3 ? 'h4' : 'h3'} /> : null}
      <div className="actions justify-center">
        {action}
        {actions}
        <Suspense fallback={null}>
          <Extra part="more" {...props} />
        </Suspense>
      </div>
    </div>
  );
}

function Steps({ steps, level: H }: { steps: EmptyStep[]; level: 'h3' | 'h4' }) {
  return (
    <div className="w-full max-w-xl overflow-hidden rounded-card border border-border bg-card text-left">
      <H className="m-0 px-4 py-3 text-base font-semibold">Ready to get started?</H>
      <ul className="m-0 list-none p-0">
        {steps.map(({ icon: Icon, title, description, action }) => (
          <li key={title} className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-3">
            {Icon ? (
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-primary/10 text-primary" aria-hidden="true">
                <Icon className="h-5 w-5" />
              </span>
            ) : null}
            {/* On a phone the button drops under the text instead of squeezing it into a narrow column */}
            <span className="min-w-0 flex-[1_1_11rem]">
              <span className="block font-medium text-foreground">{title}</span>
              <span className="block text-sm text-muted-foreground">{description}</span>
            </span>
            {action}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Hides a list's chrome (filters, smart lists, export, pagination) while there is nothing to
 * show: `<EmptyChrome empty={total === 0 && !filtered}>…</EmptyChrome>`. Keep the chrome when a
 * filter emptied the list, so the filter can be cleared.
 */
export function EmptyChrome({ empty, children }: { empty: boolean; children: ReactNode }) {
  return empty ? null : <>{children}</>;
}
